import React, { useState } from 'react';
import { Plus, Trash2, Edit, Save, ArrowUp, ArrowDown, Check, Compass } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { RichTextEditor } from '../RichTextEditor';
import { plural } from '../../lib/utils';

export function AdminJourneyTab({ journeyStages, journeyLoading, handleSaveJourneyStage }) {
  const [editDialog, setEditDialog] = useState(false);
  const [editStage, setEditStage] = useState(null);   // { id, name, content, checklist: [] }
  const [newItemText, setNewItemText] = useState('');
  const [saving, setSaving] = useState(false);

  const openEdit = (stage) => {
    setEditStage({
      id: stage.id,
      name: stage.name,
      color: stage.color,
      content: stage.content || '',
      checklist: (stage.checklist || []).map(i => ({ ...i })),
    });
    setNewItemText('');
    setEditDialog(true);
  };

  const addItem = () => {
    const text = newItemText.trim();
    if (!text) return;
    setEditStage(p => ({ ...p, checklist: [...p.checklist, { id: '', text, order: p.checklist.length }] }));
    setNewItemText('');
  };

  const updateItem = (idx, text) =>
    setEditStage(p => ({ ...p, checklist: p.checklist.map((it, i) => (i === idx ? { ...it, text } : it)) }));

  const removeItem = (idx) =>
    setEditStage(p => ({ ...p, checklist: p.checklist.filter((_, i) => i !== idx) }));

  const moveItem = (idx, dir) => {
    setEditStage(p => {
      const target = idx + dir;
      if (target < 0 || target >= p.checklist.length) return p;
      const next = [...p.checklist];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...p, checklist: next };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const ok = await handleSaveJourneyStage(editStage.id, {
      content: editStage.content,
      checklist: editStage.checklist
        .filter(i => i.text && i.text.trim())
        .map((i, idx) => ({ id: i.id || '', text: i.text.trim(), order: idx })),
    });
    setSaving(false);
    if (ok) setEditDialog(false);
  };

  return (
    <>
      <div className="mb-4">
        <p className="text-sm text-bloom-sub">
          Obsah průvodce pro jednotlivé fáze tranzice. Text i kroky se zobrazí uživatelům na stránce fáze.
        </p>
      </div>

      <div className="space-y-2">
        {journeyLoading && journeyStages.length === 0 ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : journeyStages.length === 0 ? (
          <p className="text-sm text-bloom-sub text-center py-8">Žádné fáze. Zkontrolujte, že se seed dat provedl.</p>
        ) : journeyStages.map(stage => {
          const hasContent = !!(stage.content && stage.content.trim());
          const itemCount = (stage.checklist || []).length;
          return (
            <Card key={stage.id} className="bg-white border-border/50" data-testid={`admin-journey-stage-${stage.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${stage.color}33` }}>
                  <Compass className="w-4 h-4" style={{ color: stage.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-bloom-text">{stage.name}</p>
                  <p className="text-xs text-bloom-sub flex items-center gap-1.5 flex-wrap">
                    {hasContent ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600"><Check className="w-3 h-3" />Text vyplněn</span>
                    ) : (
                      <span className="text-amber-600">Text zatím prázdný</span>
                    )}
                    <span>·</span>
                    <span>{itemCount} {plural(itemCount, 'krok', 'kroky', 'kroků')}</span>
                  </p>
                </div>
                <Button variant="ghost" size="icon" className="text-bloom-violet hover:bg-bloom-violet/10 h-8 w-8 shrink-0"
                  onClick={() => openEdit(stage)} data-testid={`edit-journey-stage-${stage.id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={editDialog} onOpenChange={open => { setEditDialog(open); if (!open) setEditStage(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg text-bloom-text">
              Průvodce: {editStage?.name}
            </DialogTitle>
            <DialogDescription>
              Uprav text průvodce a kroky, které uvidí uživatelé v této fázi.
            </DialogDescription>
          </DialogHeader>
          {editStage && (
            <form onSubmit={save} className="space-y-4">
              <div className="space-y-2">
                <Label>Text průvodce</Label>
                <RichTextEditor
                  key={editStage.id}
                  value={editStage.content}
                  onChange={v => setEditStage(p => ({ ...p, content: v }))}
                  rows={10}
                  placeholder="Ukázkový nadpis a text, který se zobrazí uživatelům v této fázi..."
                />
              </div>

              <div className="space-y-2">
                <Label>Kroky ({editStage.checklist.length} {plural(editStage.checklist.length, 'krok', 'kroky', 'kroků')})</Label>
                <div className="space-y-1.5">
                  {editStage.checklist.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-1.5" data-testid={`journey-checklist-row-${idx}`}>
                      <Input value={item.text} onChange={e => updateItem(idx, e.target.value)}
                        className="flex-1" data-testid={`journey-checklist-input-${idx}`} />
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-bloom-sub"
                        onClick={() => moveItem(idx, -1)} disabled={idx === 0} title="Nahoru"
                        data-testid={`journey-checklist-up-${idx}`}>
                        <ArrowUp className="w-3.5 h-3.5" />
                      </Button>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-bloom-sub"
                        onClick={() => moveItem(idx, 1)} disabled={idx === editStage.checklist.length - 1} title="Dolů"
                        data-testid={`journey-checklist-down-${idx}`}>
                        <ArrowDown className="w-3.5 h-3.5" />
                      </Button>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-destructive"
                        onClick={() => removeItem(idx)} title="Smazat"
                        data-testid={`journey-checklist-remove-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  {editStage.checklist.length === 0 && (
                    <p className="text-xs text-bloom-sub py-2">Zatím žádné kroky.</p>
                  )}
                </div>
                <div className="flex gap-1.5 pt-1">
                  <Input value={newItemText} onChange={e => setNewItemText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
                    placeholder="Přidat krok..." className="flex-1" data-testid="journey-checklist-new-input" />
                  <Button type="button" variant="outline" className="border-bloom-violet/30 text-bloom-violet shrink-0"
                    onClick={addItem} data-testid="journey-checklist-add-btn">
                    <Plus className="w-4 h-4 mr-1" />Přidat
                  </Button>
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit" className="bg-bloom-violet text-white flex-1" disabled={saving} data-testid="journey-stage-save-btn">
                  <Save className="w-4 h-4 mr-1.5" />{saving ? 'Ukládám...' : 'Uložit'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditDialog(false)}>Zrušit</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
